import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { Observable, finalize, map } from 'rxjs';
import { reportInvalidFormFields } from '@app/utilities';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from '@card-software/library';
import { environment } from '../../../../environments/environment';
import {
  ApAdvanceRequest,
  ApAdvanceRequestInsert,
  ApAdvanceRequestUpdate,
  ApPaymentRequestStatus,
} from '../../../models/ap-advance-request/ap-advance-request';
import { ApAdvanceRequestFormGroup, ApAdvanceRequestLineFormGroup } from '../../../models/ap-advance-request/ap-advance-request-form';
import { ApAdvanceRequestProxyService } from '../../../proxies/ap-advance-request.proxy.service';
import { PurchaseOrderProxyService, PURCHASE_ORDER_STATUS_CANCELLED } from '../../../proxies/purchase-order.proxy.service';
import { BusinessProxyService } from '../../../proxies/business.proxy.service';
import { Business, BusinessEntityType } from '../../../models/business';
import { ProviderBankAccount } from '../../../models/provider/provider-details';
import { EnumService } from '../../../services/enum.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { AccountsPayableRoles } from '../../../models/roles/roles-group';
import { currencyLabelFromApi } from '../../../utilities/currency.util';
import { Currency } from '../../../models/ap-document/ap-document';

interface SelectOption {
  label: string;
  value: string;
}

interface PresetLine {
  amount: number;
  number: string;
}

@Component({
  selector: 'app-ap-advance-request-details',
  templateUrl: './ap-advance-request-details.component.html',
  styleUrl: './ap-advance-request-details.component.scss',
})
export class ApAdvanceRequestDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly service = inject(ApAdvanceRequestProxyService);
  private readonly purchaseOrderService = inject(PurchaseOrderProxyService);
  private readonly businessService = inject(BusinessProxyService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly enumService = inject(EnumService);
  private readonly translate = inject(TranslateService);
  private readonly auth = inject(UserAuthenticationService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  readonly ApPaymentRequestStatus = ApPaymentRequestStatus;

  isLoading = signal(0);
  isNew = signal(true);
  currentId = signal<string | null>(null);
  status = signal<number>(ApPaymentRequestStatus.Draft);
  headerTitle = signal<string>(this.t('ApAdvanceRequest_New', 'New advance request'));
  spawnedAdvanceId = signal<string | null>(null);

  bankOptions = signal<SelectOption[]>([]);
  /** The accounts behind those options: the warning below needs each one's currency, not just its label. */
  private readonly providerBanks = signal<ProviderBankAccount[]>([]);
  /** The chosen account id, mirrored from the form so the warning reacts to picking one. */
  private readonly selectedBankId = signal<string | null>(null);

  /**
   * Warns when the account being approved is one the advance will be refused for (#5373).
   *
   * <p>Paying a foreign-currency order into a dollar account is allowed (Â§8) â€” it is ordinary trade
   * practice with international suppliers, and the rate falls out of what left the bank. It is refused at
   * post only when the provider ALSO holds an account in the order's currency, which is how the wrong one
   * gets picked: by being flagged primary. The approver is the one who can tell those apart, so this
   * cautions rather than blocks, and the destination freezes here â€” the request cannot be edited once
   * approved.</p>
   *
   * <p>Only the one rule: the three-currency refusal needs the funding account, which a request has not
   * chosen yet.</p>
   */
  readonly destinationWarning = computed<string | null>(() => {
    const bank = this.providerBanks().find((b) => b.id === this.selectedBankId());
    if (!bank) return null;
    const obligation = this.currency();
    const arrival = bank.currency as number;
    // The books' currency. USD everywhere today, as on the advance itself.
    const functional: number = Currency.USD;
    if (obligation === functional || arrival !== functional || obligation === arrival) return null;

    return this.t(
      'ApAdvanceRequest_Bank_CurrencyWarning',
      'These purchase orders are in {obligation} and this account is in {arrival}. That is allowed, but check it is the right account â€” where a provider holds one in {obligation} too, the {arrival} account is usually picked by accident.',
    )
      .replace(/{obligation}/g, this.currencyLabels[obligation] ?? '')
      .replace(/{arrival}/g, this.currencyLabels[arrival] ?? '');
  });

  total = signal(0);
  selectedCount = signal(0);

  readonly isApprover = this.auth.roles.includes(AccountsPayableRoles.AccountsPayableApprover);

  private statusLabels: Record<number, string> = {};
  private currencyLabels: Record<number, string> = {};
  private currency = signal<number>(0);

  readonly isDraft = computed(() => this.status() === ApPaymentRequestStatus.Draft);
  readonly isPendingApproval = computed(() => this.status() === ApPaymentRequestStatus.PendingApproval);
  readonly isPersisted = computed(() => !this.isNew() && !!this.currentId());
  readonly isEditable = computed(() => this.isDraft() || (this.isPendingApproval() && this.isApprover));

  readonly form: FormGroup<ApAdvanceRequestFormGroup> = this.formBuilder.group<ApAdvanceRequestFormGroup>({
    provider: new FormControl<Business | null>(null, [Validators.required]),
    selectedProviderBankId: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    lines: this.formBuilder.array<FormGroup<ApAdvanceRequestLineFormGroup>>([]),
  });

  get lines(): FormArray<FormGroup<ApAdvanceRequestLineFormGroup>> {
    return this.form.controls.lines;
  }

  private readonly fieldLabels: Record<string, string> = {
    provider: 'ApPayment_Provider',
    lines: 'ApAdvanceRequest_Lines',
  };

  constructor() {
    this.statusLabels = this.enumService.getEnum('ApPaymentRequestStatus').values as Record<number, string>;
    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.lines.valueChanges.subscribe(() => this.recomputeTotals());
    this.form.controls.selectedProviderBankId.valueChanges.subscribe((id) => this.selectedBankId.set(id));
  }

  ngOnInit(): void {
    const id = this.route.snapshot.queryParamMap.get('id');
    if (id && id !== 'new') {
      this.isNew.set(false);
      this.currentId.set(id);
      this.loadRequest(id);
    } else {
      this.breadcrumbService.setCurrent('Common_New');
    }
  }

  // ---- helpers ----
  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  currencyLabel(): string {
    return this.currencyLabels[this.currency()] ?? '';
  }

  statusLabelText(): string {
    return this.statusLabels[this.status()] ?? '';
  }

  statusSeverity(): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (this.status()) {
      case ApPaymentRequestStatus.Approved:
        return 'success';
      case ApPaymentRequestStatus.PendingApproval:
        return 'warn';
      case ApPaymentRequestStatus.Rejected:
        return 'danger';
      case ApPaymentRequestStatus.Cancelled:
        return 'secondary';
      default:
        return 'info';
    }
  }

  // ---- provider ----
  public fetchProviders = (query: string): Observable<Business[]> =>
    this.businessService
      .search({ name: query, code: query, businessTypes: [BusinessEntityType.Provider] }, 1, 8, undefined, undefined, true)
      .pipe(map((response) => response.data?.items ?? []));

  onProviderSelected(provider: Business): void {
    if (!provider?.id) return;
    this.loadProviderBanks(provider);
    this.loadProviderPurchaseOrders(provider.id);
  }

  onProviderCleared(): void {
    this.providerBanks.set([]);
    this.bankOptions.set([]);
    this.form.controls.selectedProviderBankId.setValue(null);
    this.lines.clear();
    this.recomputeTotals();
  }

  private loadProviderBanks(provider: Business): void {
    const apply = (banks: ProviderBankAccount[]) => {
      this.providerBanks.set(banks);
      this.bankOptions.set(banks.filter((b) => !!b.id).map((b) => ({ label: this.bankLabel(b), value: b.id! })));
      const primary = banks.find((b) => b.isPrimary) ?? banks[0];
      if (primary?.id && !this.form.controls.selectedProviderBankId.value) {
        this.form.controls.selectedProviderBankId.setValue(primary.id);
      }
    };
    if (provider.providerDetails?.banks?.length) {
      apply(provider.providerDetails.banks);
      return;
    }
    if (!provider.id) return;
    this.businessService.getBusinessById(provider.id).subscribe({
      next: (response) => apply(response.data?.providerDetails?.banks ?? []),
      error: () => {
        this.providerBanks.set([]);
        this.bankOptions.set([]);
      },
    });
  }

  /** Alias/bank, currency, account number. The currency is the field that decides whether paying this
   *  account converts, so it belongs in the label the approver picks from. */
  private bankLabel(bank: ProviderBankAccount): string {
    const parts = [bank.alias || bank.bankName, currencyLabelFromApi(bank.currency), bank.accountNumber].filter((s) => s);
    return parts.join(' Â· ') || (bank.id ?? '');
  }

  // ---- purchase orders ----
  private loadProviderPurchaseOrders(providerId: string, preset?: Map<string, PresetLine>): void {
    this.startLoading();
    this.purchaseOrderService
      .search({ providerReference: providerId }, 1, 200)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          this.lines.clear();
          // Never offer a cancelled PO for an advance (#4516).
          const pos = (response.data?.items ?? []).filter((po) => po.status !== PURCHASE_ORDER_STATUS_CANCELLED);
          if (pos.length && pos[0].currency != null) this.currency.set(pos[0].currency);
          // Once the request is locked (approved/rejected/â€¦), show only the POs that were actually requested,
          // not the provider's whole open-PO list.
          const showOnlySelected = !!preset && !this.isEditable();
          const seen = new Set<string>();
          for (const po of pos) {
            const poId = po.id ?? '';
            if (showOnlySelected && !preset!.has(poId)) continue;
            seen.add(poId);
            const ordered = (po.items ?? []).reduce((sum, i) => sum + (i.totalPrice ?? 0), 0);
            const presetLine = preset?.get(poId);
            this.lines.push(this.buildLineGroup(poId, po.purchaseOrderNumber ?? '', ordered, presetLine));
          }
          // Preserve requested POs no longer returned by the live search.
          if (preset) {
            for (const [poId, info] of preset) {
              if (seen.has(poId)) continue;
              this.lines.push(this.buildLineGroup(poId, info.number, 0, info));
            }
          }
          this.recomputeTotals();
          if (!this.isEditable()) this.form.disable({ emitEvent: false });
        },
        error: () => {
          this.lines.clear();
          this.recomputeTotals();
        },
      });
  }

  private buildLineGroup(poId: string, poNumber: string, ordered: number, preset?: PresetLine): FormGroup<ApAdvanceRequestLineFormGroup> {
    return this.formBuilder.group<ApAdvanceRequestLineFormGroup>({
      purchaseOrderReference: new FormControl<string>(poId, { nonNullable: true }),
      purchaseOrderNumber: new FormControl<string>(poNumber, { nonNullable: true }),
      selected: new FormControl<boolean>(!!preset, { nonNullable: true }),
      amount: new FormControl<number>(preset?.amount ?? 0, { nonNullable: true }),
      ordered: new FormControl<number>(Math.round(ordered * 100) / 100, { nonNullable: true }),
    });
  }

  private recomputeTotals(): void {
    let total = 0;
    let selected = 0;
    for (const row of this.lines.controls) {
      if (row.controls.selected.value) {
        total += row.controls.amount.value ?? 0;
        selected++;
      }
    }
    this.total.set(Math.round(total * 100) / 100);
    this.selectedCount.set(selected);
  }

  // ---- load existing ----
  private loadRequest(id: string): void {
    this.startLoading();
    this.service
      .getById(id)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) this.patchFromRequest(response.data);
        },
        error: (error) => console.error('Error loading advance request:', error),
      });
  }

  private patchFromRequest(data: ApAdvanceRequest): void {
    this.status.set(data.status);
    this.currency.set(data.currency);
    this.headerTitle.set(`${this.t('ApAdvanceRequest', 'Advance request')} ${data.number ?? ''}`.trim());
    this.breadcrumbService.setCurrent(data.number);
    this.spawnedAdvanceId.set(data.spawnedApAdvanceReference ?? null);

    const provider: Business = {
      id: data.providerReference,
      name: data.providerLegalName ?? '',
      code: data.providerCode,
      businessTypes: [BusinessEntityType.Provider],
    };
    this.form.patchValue(
      { provider, selectedProviderBankId: data.selectedProviderBankId ?? null, notes: data.notes ?? null },
      { emitEvent: false },
    );
    // The patch is silent, so the mirror the currency warning reads does not follow it.
    this.selectedBankId.set(data.selectedProviderBankId ?? null);
    this.form.controls.provider.disable({ emitEvent: false }); // provider is locked after creation

    this.loadProviderBanks(provider);

    const preset = new Map<string, PresetLine>();
    for (const line of data.lines ?? []) {
      preset.set(line.purchaseOrderReference, { amount: line.amount, number: line.purchaseOrderNumber });
    }
    this.loadProviderPurchaseOrders(data.providerReference, preset);
  }

  // ---- build payloads ----
  private buildLines(): { purchaseOrderReference: string; amount: number }[] {
    return this.lines.controls
      .filter((r) => r.controls.selected.value && (r.controls.amount.value ?? 0) > 0)
      .map((r) => ({ purchaseOrderReference: r.controls.purchaseOrderReference.value, amount: r.controls.amount.value }));
  }

  private validate(): boolean {
    if (this.form.controls.provider.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    if (this.buildLines().length === 0) {
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return false;
    }
    return true;
  }

  // ---- actions ----
  saveDraft(): void {
    if (!this.validate()) return;
    this.startLoading();

    const id = this.currentId();
    if (!this.isNew() && id) {
      const update: ApAdvanceRequestUpdate = {
        selectedProviderBankId: this.form.controls.selectedProviderBankId.value,
        notes: this.form.controls.notes.value,
        lines: this.buildLines(),
      };
      this.service
        .update(id, update)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (response) => {
            if (response.data) this.patchFromRequest(response.data);
          },
          error: (error) => console.error('Error updating advance request:', error),
        });
      return;
    }

    const provider = this.form.controls.provider.value;
    const insert: ApAdvanceRequestInsert = {
      financialCompanyReference: '', // API resolves from the current site
      providerReference: provider?.id ?? '',
      selectedProviderBankId: this.form.controls.selectedProviderBankId.value,
      notes: this.form.controls.notes.value,
      lines: this.buildLines(),
    };
    this.service
      .createDraft(insert)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          const created = response.data;
          if (created?.id) {
            this.isNew.set(false);
            this.currentId.set(created.id);
            this.router.navigate([], { queryParams: { id: created.id }, replaceUrl: true });
            this.patchFromRequest(created);
          }
        },
        error: (error) => console.error('Error creating advance request:', error),
      });
  }

  submit(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvanceRequest_Submit_Header', 'Submit for approval'),
      message: this.t('ApAdvanceRequest_Submit_Message', 'Submit this advance request for approval?'),
      icon: 'pi pi-send',
      accept: () => this.runAction(this.service.submit(id)),
    });
  }

  approve(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvanceRequest_Approve_Header', 'Approve request'),
      message: this.t('ApAdvanceRequest_Approve_Message', 'Approving creates a draft advance for the executing department. Continue?'),
      icon: 'pi pi-check',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => this.runAction(this.service.approve(id)),
    });
  }

  reject(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvanceRequest_Reject_Header', 'Reject request'),
      message: this.t('ApAdvanceRequest_Reject_Message', 'Reject this advance request?'),
      icon: 'pi pi-times',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.runAction(this.service.reject(id)),
    });
  }

  cancel(): void {
    const id = this.currentId();
    if (!id) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvanceRequest_Cancel_Header', 'Cancel request'),
      message: this.t('ApAdvanceRequest_Cancel_Message', 'Cancel this advance request?'),
      icon: 'pi pi-ban',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.runAction(this.service.cancel(id)),
    });
  }

  private runAction(action: ReturnType<ApAdvanceRequestProxyService['submit']>): void {
    this.startLoading();
    action.pipe(finalize(() => this.stopLoading())).subscribe({
      next: (response) => {
        if (response.data) this.patchFromRequest(response.data);
      },
      error: (error) => console.error('Error on advance request action:', error),
    });
  }

  openSpawnedAdvance(): void {
    const advanceId = this.spawnedAdvanceId();
    if (advanceId) this.router.navigate(['/ap-advance-details'], { queryParams: { id: advanceId } });
  }

  /**
   * Opens the purchase order in the Order UI in a new tab.
   *
   * <p>The JWT is deliberately NOT passed as ?token=: it pushed the URL past what the edge accepts and the
   * navigation failed outright. The Order UI adopts the session it already holds; if it holds none, its guard
   * sends the user to login with a returnUrl and they land back on this purchase order.</p>
   */
  openPurchaseOrder(purchaseOrderReference: string | null | undefined): void {
    if (!purchaseOrderReference) return;
    const url = `${environment.domainUrl.order}/purchase-order-details?id=${purchaseOrderReference}`;
    window.open(url, '_blank');
  }

  back(): void {
    this.router.navigate(['/ap-advance-requests']);
  }

  getRowGroup(row: unknown): FormGroup<ApAdvanceRequestLineFormGroup> {
    return row as FormGroup<ApAdvanceRequestLineFormGroup>;
  }

  /**
   * Deletes this draft, after confirming (#5223).
   *
   * <p>Offered only on a SAVED draft: there is nothing to delete before the first save. The API applies the
   * same draft-only rule, so a record that moved on in another tab is answered by that refusal rather than
   * by whatever this screen last painted â€” the button is a convenience, not the guard.</p>
   *
   * <p>Hard delete: a draft has posted nothing, so there is no ledger entry to keep and nothing a surviving
   * row would explain. Hence the explicit warning that it cannot be undone.</p>
   */
  confirmDelete(): void {
    const id = this.currentId();
    if (!id || !this.isDraft()) return;
    this.confirmationService.confirm({
      header: this.t('ApAdvanceRequest_Delete_Title', 'Delete draft'),
      message: this.t('ApAdvanceRequest_Delete_Confirm', 'Delete this draft advance request? This cannot be undone.'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.t('Delete', 'Delete'),
      rejectLabel: this.t('Cancel', 'Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteDraft(id),
    });
  }

  private deleteDraft(id: string): void {
    this.service.delete(id).subscribe({
      next: () => {
        GlobalMessageUtil.success(
          this.t('ApAdvanceRequest_Delete_Done_Title', 'Draft deleted'),
          this.t('ApAdvanceRequest_Delete_Done', 'The draft was deleted.'),
        );
        this.router.navigate(['/ap-advance-requests']);
      },
    });
  }
}
